import React, { useState, useEffect } from 'react';
import {
  Modal, ModalOverlay, ModalContent, ModalHeader, ModalFooter, ModalBody, ModalCloseButton,
  Button, FormControl, FormLabel, Input, Select, Textarea, VStack,
  HStack, useToast, Badge, Box, Text, SimpleGrid, Table, Thead, Tbody, Tr, Th, Td,
  Image, Wrap, WrapItem, Alert, AlertIcon,
} from '@chakra-ui/react';
import { AddIcon } from '@chakra-ui/icons';
import { InspeccionOP, crearInspeccionOP, actualizarInspeccionOP, obtenerInspeccionesOP, subirFotosInspeccionOP } from '../../../../api/opTramiteApi';

interface Props {
  isOpen: boolean;
  onClose: () => void;
  idTramite: number;
  folioTramite: string;
}

const RESULTADO_COLORS: Record<string, string> = {
  PENDIENTE: 'yellow', APROBADO: 'green', REPROBADO: 'red', CANCELADO: 'gray',
};

const API_URL = process.env.REACT_APP_API_URL || 'http://127.0.0.1:5000';

const ModalInspeccionOP: React.FC<Props> = ({ isOpen, onClose, idTramite, folioTramite }) => {
  const [inspecciones, setInspecciones] = useState<InspeccionOP[]>([]);
  const [modo, setModo] = useState<'lista' | 'nueva' | 'editar'>('lista');
  const [seleccionada, setSeleccionada] = useState<InspeccionOP | null>(null);
  const [fotos, setFotos] = useState<File[]>([]);
  const [loading, setLoading] = useState(false);
  const toast = useToast();

  const [form, setForm] = useState({
    tipoInspeccion: '',
    inspector: '',
    fechaProgramada: '',
    fechaRealizada: '',
    resultado: 'PENDIENTE',
    descripcion: '',
    observaciones: '',
    recomendaciones: '',
  });

  const cargar = async () => {
    try {
      const data = await obtenerInspeccionesOP(idTramite);
      setInspecciones(data);
    } catch {
      toast({ title: 'Error al cargar inspecciones', status: 'error' });
    }
  };

  useEffect(() => {
    if (isOpen) {
      cargar();
      setFotos([]);
      setModo('lista');
    }
  }, [isOpen]);

  const set = (f: string, v: string) => setForm(prev => ({ ...prev, [f]: v }));

  const handleNueva = () => {
    setSeleccionada(null);
    setFotos([]);
    setForm({ tipoInspeccion: '', inspector: '', fechaProgramada: '', fechaRealizada: '', resultado: 'PENDIENTE', descripcion: '', observaciones: '', recomendaciones: '' });
    setModo('nueva');
  };

  const handleEditar = (insp: InspeccionOP) => {
    setSeleccionada(insp);
    setFotos([]);
    setForm({
      tipoInspeccion: insp.tipoInspeccion || '',
      inspector: insp.inspector || '',
      fechaProgramada: insp.fechaProgramada?.split('T')[0] || '',
      fechaRealizada: insp.fechaRealizada?.split('T')[0] || '',
      resultado: insp.resultado || 'PENDIENTE',
      descripcion: insp.descripcion || '',
      observaciones: insp.observaciones || '',
      recomendaciones: insp.recomendaciones || '',
    });
    setModo('editar');
  };

  const evidencias = (insp?: InspeccionOP | null) => {
    if (!insp) return [];
    return [insp.rutaFoto1, insp.rutaFoto2, insp.rutaFoto3].filter(Boolean) as string[];
  };

  const fotoUrl = (ruta: string) => {
    if (ruta.startsWith('http')) return ruta;
    if (ruta.startsWith('/uploads/')) return `${API_URL}${ruta}`;
    return `${API_URL}/uploads/op-inspecciones/${ruta}`;
  };

  const handleFotosChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    setFotos(Array.from(event.target.files || []).slice(0, 3));
  };

  const handleGuardar = async () => {
    if (!form.fechaProgramada) {
      toast({ title: 'La fecha programada es obligatoria', status: 'error' });
      return;
    }
    setLoading(true);
    try {
      let guardada: InspeccionOP | null = null;
      if (modo === 'nueva') {
        guardada = await crearInspeccionOP({ idTramite, ...form });
      } else if (seleccionada) {
        guardada = await actualizarInspeccionOP(seleccionada.id, form);
      }
      if (guardada && fotos.length > 0) {
        await subirFotosInspeccionOP(guardada.id, fotos);
      }
      toast({ title: 'Inspección guardada', status: 'success' });
      cargar();
      setFotos([]);
      setModo('lista');
    } catch (err: any) {
      toast({ title: 'Error', description: err.message, status: 'error' });
    } finally {
      setLoading(false);
    }
  };

  return (
    <Modal isOpen={isOpen} onClose={onClose} size="4xl" scrollBehavior="inside">
      <ModalOverlay />
      <ModalContent>
        <ModalHeader bg="#5b182b" color="white" borderTopRadius="md">
          Inspecciones — Trámite {folioTramite}
        </ModalHeader>
        <ModalCloseButton color="white" />

        <ModalBody pt={4}>
          {modo === 'lista' ? (
            <Box>
              <HStack mb={3} justify="space-between">
                <Text fontWeight="bold">{inspecciones.length} inspeccion(es) registrada(s)</Text>
                <Button size="sm" leftIcon={<AddIcon />} colorScheme="green" onClick={handleNueva}>
                  Programar Inspección
                </Button>
              </HStack>

              {inspecciones.length === 0 ? (
                <Text color="gray.500" textAlign="center" py={6}>No hay inspecciones registradas</Text>
              ) : (
                <Table size="sm" variant="striped">
                  <Thead>
                    <Tr>
                      <Th>Folio</Th>
                      <Th>Tipo</Th>
                      <Th>Inspector</Th>
                      <Th>Fecha Programada</Th>
                      <Th>Resultado</Th>
                      <Th>Evidencias</Th>
                      <Th>Acciones</Th>
                    </Tr>
                  </Thead>
                  <Tbody>
                    {inspecciones.map(i => (
                      <Tr key={i.id}>
                        <Td fontFamily="mono">{i.folio}</Td>
                        <Td>{i.tipoInspeccion || '—'}</Td>
                        <Td>{i.inspector || '—'}</Td>
                        <Td>{new Date(i.fechaProgramada).toLocaleDateString('es-MX')}</Td>
                        <Td>
                          <Badge colorScheme={RESULTADO_COLORS[i.resultado || 'PENDIENTE'] || 'gray'}>
                            {i.resultado || 'PENDIENTE'}
                          </Badge>
                        </Td>
                        <Td>
                          <Badge colorScheme={evidencias(i).length > 0 ? 'blue' : 'gray'}>
                            {evidencias(i).length} foto(s)
                          </Badge>
                        </Td>
                        <Td>
                          <Button size="xs" onClick={() => handleEditar(i)}>Editar</Button>
                        </Td>
                      </Tr>
                    ))}
                  </Tbody>
                </Table>
              )}
            </Box>
          ) : (
            <VStack spacing={4} align="stretch">
              <HStack>
                <Button size="sm" variant="outline" onClick={() => setModo('lista')}>← Regresar</Button>
                <Text fontWeight="bold">{modo === 'nueva' ? 'Nueva Inspección' : 'Editar Inspección'}</Text>
              </HStack>

              <SimpleGrid columns={{ base: 1, md: 2 }} spacing={4}>
                <FormControl>
                  <FormLabel>Tipo de Inspección</FormLabel>
                  <Select value={form.tipoInspeccion} onChange={e => set('tipoInspeccion', e.target.value)}>
                    <option value="">Seleccione...</option>
                    <option value="Verificación inicial">Verificación inicial</option>
                    <option value="Seguimiento">Seguimiento</option>
                    <option value="Final">Final</option>
                    <option value="Urgente">Urgente</option>
                  </Select>
                </FormControl>
                <FormControl>
                  <FormLabel>Inspector</FormLabel>
                  <Input value={form.inspector} onChange={e => set('inspector', e.target.value)} />
                </FormControl>
              </SimpleGrid>

              <SimpleGrid columns={{ base: 1, md: 2 }} spacing={4}>
                <FormControl isRequired>
                  <FormLabel>Fecha Programada</FormLabel>
                  <Input type="date" value={form.fechaProgramada} onChange={e => set('fechaProgramada', e.target.value)} />
                </FormControl>
                <FormControl>
                  <FormLabel>Fecha Realizada</FormLabel>
                  <Input type="date" value={form.fechaRealizada} onChange={e => set('fechaRealizada', e.target.value)} />
                </FormControl>
              </SimpleGrid>

              <FormControl>
                <FormLabel>Resultado</FormLabel>
                <Select value={form.resultado} onChange={e => set('resultado', e.target.value)}>
                  <option value="PENDIENTE">Pendiente</option>
                  <option value="APROBADO">Aprobado</option>
                  <option value="REPROBADO">Reprobado</option>
                  <option value="CANCELADO">Cancelado</option>
                </Select>
              </FormControl>

              <FormControl>
                <FormLabel>Descripción</FormLabel>
                <Textarea value={form.descripcion} onChange={e => set('descripcion', e.target.value)} rows={3} />
              </FormControl>

              <FormControl>
                <FormLabel>Observaciones</FormLabel>
                <Textarea value={form.observaciones} onChange={e => set('observaciones', e.target.value)} rows={3} />
              </FormControl>

              <FormControl>
                <FormLabel>Recomendaciones</FormLabel>
                <Textarea value={form.recomendaciones} onChange={e => set('recomendaciones', e.target.value)} rows={2} />
              </FormControl>

              <Box borderWidth="1px" borderRadius="md" p={4} bg="gray.50">
                <FormControl>
                  <FormLabel>Evidencias fotograficas de la inspeccion</FormLabel>
                  <Input
                    type="file"
                    accept="image/jpeg,image/png,image/webp"
                    multiple
                    onChange={handleFotosChange}
                    bg="white"
                  />
                  <Text fontSize="xs" color="gray.500" mt={1}>
                    Maximo 3 imagenes. Formatos permitidos: JPG, PNG o WEBP. Tamano maximo por archivo: 10 MB.
                  </Text>
                </FormControl>

                {fotos.length > 0 && (
                  <Alert status="info" mt={3} borderRadius="md">
                    <AlertIcon />
                    Se subiran {fotos.length} foto(s) al guardar la inspeccion.
                  </Alert>
                )}

                {evidencias(seleccionada).length > 0 && (
                  <Box mt={4}>
                    <Text fontSize="sm" fontWeight="bold" mb={2}>Evidencias cargadas</Text>
                    <Wrap spacing={3}>
                      {evidencias(seleccionada).map((ruta, idx) => (
                        <WrapItem key={`${ruta}-${idx}`}>
                          <Box
                            as="a"
                            href={fotoUrl(ruta)}
                            target="_blank"
                            rel="noreferrer"
                            borderWidth="1px"
                            borderRadius="md"
                            overflow="hidden"
                            bg="white"
                          >
                            <Image src={fotoUrl(ruta)} alt={`Evidencia ${idx + 1}`} boxSize="110px" objectFit="cover" />
                            <Text fontSize="xs" textAlign="center" py={1}>Foto {idx + 1}</Text>
                          </Box>
                        </WrapItem>
                      ))}
                    </Wrap>
                  </Box>
                )}
              </Box>
            </VStack>
          )}
        </ModalBody>

        <ModalFooter borderTop="1px solid #eee">
          <Button variant="ghost" mr={3} onClick={onClose}>Cerrar</Button>
          {modo !== 'lista' && (
            <Button bg="#5b182b" color="white" _hover={{ bg: '#8b1f33' }} onClick={handleGuardar} isLoading={loading}>
              Guardar Inspección
            </Button>
          )}
        </ModalFooter>
      </ModalContent>
    </Modal>
  );
};

export default ModalInspeccionOP;
